'use client';

/**
 * HomeAttentionCard — "Needs Attention" triage list.
 *
 * `home_attention` ships BOTH a prose `summary` (Layer 3 LLM) AND a
 * structured `items: AttentionItem[]` (title / description / severity /
 * entity linkage). This card renders the ITEMS as scannable, clickable
 * rows — severity dot first, issue + context, chevron through to the
 * entity — matching the Recent list directly below so the two read as one
 * system. Falls back to the prose summary if no items, then all-clear.
 *
 * Presentation-only: the BFF already ships everything; the previous version
 * just rendered the summary sentence and threw the items away.
 *
 * @see klyde_edge/ui_config/handlers/synthesis.py — home_attention
 * @see klyde_edge/ui_config/handlers/synthesis_responses.py — AttentionItem
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Home
 * @layer: presentation
 */

import { ChevronRight } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useLanguageStore } from '@/lib/stores/language-store';
import { switchExplorerMode } from '@/lib/stores/tab-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { CardShell } from '../card-shell';
import { useAffordance } from '@/lib/stores/total-matrix-store';

interface AttentionItem {
  title: string;
  description: string;
  entity_sid: string | null;
  entity_type: string | null;
  severity: string; // high | medium | low
  action: string | null;
}

interface HomeAttentionResponse {
  summary: string;
  items: AttentionItem[];
  labels: Record<string, string>;
}

// Severity → leading dot colour. No count pill: the response carries no
// per-item count, and inventing one would fabricate data — the dot conveys
// severity, the count (where it exists) already lives in the title text.
function severityDot(severity: string): string {
  if (severity === 'high') return 'bg-destructive';
  if (severity === 'low') return 'bg-info';
  return 'bg-warning'; // medium (default)
}

export function HomeAttentionCard() {
  const lang = useLanguageStore((s) => s.lang) ?? undefined;
  const params = lang ? { lang } : {};
  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  const { data: resp, isLoading } = useStrategyData<HomeAttentionResponse>(
    'home_attention',
    params,
    true,
    60 * 60 * 1000, // 1h — LLM synthesis, manual refresh not expected
  );
  const data = resp?.success ? resp.data : undefined;
  const items = data?.items ?? [];
  const summary = data?.summary ?? '';
  const L = data?.labels ?? {};

  // Row click → Projects (mode1), selecting the linked entity when present.
  const loadingTestId = useAffordance('home.needs_attention.loading');

  const openItem = (item: AttentionItem) => {
    // switchExplorerMode's own clear-stale-selection step must run BEFORE
    // selectNode, not after — same ordering rule as openTab/setProjectContext
    // elsewhere in this codebase (whichever step clears selection has to go
    // first, or it wipes the selection the other step just set).
    switchExplorerMode('mode1');
    if (item.entity_sid) {
      selectNode({
        id: item.entity_sid,
        label: item.title,
        type: item.entity_type ?? 'project',
      });
    }
  };

  return (
    <CardShell style={{ fontSize: surfaceBase.spacious }} className="overflow-hidden h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
          <h3 className={text.heading}>{L.attention_title ?? 'Needs Attention'}</h3>
        </div>
        {items.length > 0 && (
          <button
            onClick={() => switchExplorerMode('mode1')}
            className={cn(text.muted, 'hover:text-foreground transition-colors cursor-pointer')}
          >
            {L.view_all ?? 'View all'}
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0">
        {isLoading ? (
          <div data-testid={loadingTestId} className="px-4 py-4 space-y-2.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-muted animate-pulse shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
                  <div className="h-2.5 w-1/2 rounded bg-muted animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length > 0 ? (
          // Rows — same treatment as the Recent list below, so they read as
          // one system: divider, hover, chevron, count/severity leading.
          <div className="divide-y divide-border">
            {items.map((item, i) => (
              <button
                key={i}
                onClick={() => openItem(item)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-accent/50 transition-colors group cursor-pointer"
              >
                <span className={cn('w-2 h-2 rounded-full shrink-0', severityDot(item.severity))} />
                <div className="flex-1 min-w-0">
                  <div className={cn(text.body, 'truncate')}>{item.title}</div>
                  {item.description && (
                    <div className={cn(text.muted, 'truncate')}>{item.description}</div>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground/40 shrink-0 group-hover:text-muted-foreground transition-colors" />
              </button>
            ))}
          </div>
        ) : summary ? (
          <p className={cn(text.muted, 'px-4 py-4')}>{summary}</p>
        ) : (
          <p className={cn(text.muted, 'px-4 py-4')}>{L.all_clear ?? 'All clear — nothing needs attention.'}</p>
        )}
      </div>
    </CardShell>
  );
}
